import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { ChevronDown } from "lucide-react";
import { cn } from "../../lib/utils";
import { DropdownMenu, type DropdownMenuItem } from "../Dropdown/DropdownMenu";
import { FormHeader } from "../FormHeader";
import { FormFooter } from "../FormFooter";

export interface SelectOption {
  value: string | number;
  label?: string;
  description?: string;
  leadingIcon?: React.ReactNode;
  trailingIcon?: React.ReactNode;
  isDisabled?: boolean;
  variant?: "default" | "bordered" | "primary" | "negative";
}

const selectVariants = cva(
  "relative flex items-center gap-2 border rounded-large transition-all font-functional font-size-100 leading-100",
  {
    variants: {
      size: {
        small: "h-[28px] px-3 text-xs gap-2",
        medium: "h-[36px] px-4 text-sm gap-2",
        large: "h-[44px] px-5 text-base gap-3",
      },
      validationState: {
        none: `
        border-action-outline-neutral-faded 
        hover:border-action-outline-primary-hover 
        focus-within:border-action-outline-primary-hover 
        focus-within:ring-2 
        ring-action-outline-primary-faded-hover`,
        positive: `
          border-action-outline-positive-default 
          focus-within:border-action-outline-positive-hover
          focus-within:ring-2
          ring-action-outline-positive-faded-hover`,
        negative: `border-action-outline-negative-default 
          focus-within:border-action-outline-negative-hover
          focus-within:ring-2 
          ring-action-outline-negative-faded-hover`,
      },
      isDisabled: {
        true: `
        border-[var(--border-width-thinner)]
        hover:border-action-outline-neutral-disabled
        border-action-outline-neutral-disabled
        bg-surface-fill-neutral-intense cursor-not-allowed opacity-60`,
        false: "bg-surface-fill-neutral-intense",
      },
    },
    defaultVariants: {
      size: "medium",
      validationState: "none",
      isDisabled: false,
    },
  }
);

export interface SelectProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "onChange" | "prefix"> {
  /**
   * Options to display in the select dropdown
   */
  options?: SelectOption[];
  /**
   * Selected value (controlled)
   */
  value?: string | number;
  /**
   * Default selected value (uncontrolled)
   */
  defaultValue?: string | number;
  /**
   * Callback when selection changes
   */
  onChange?: (value: string | number, option: SelectOption) => void;
  /**
   * Placeholder text when no value is selected
   */
  placeholder?: string;
  /**
   * Label for the select
   */
  label?: string;
  /**
   * Helper text below the select
   */
  helperText?: string;
  /**
   * Error text (overrides helperText when present)
   */
  errorText?: string;
  /**
   * Success text (overrides helperText when present)
   */
  successText?: string;
  /**
   * Validation state for the select
   */
  validationState?: "none" | "positive" | "negative";
  /**
   * Whether the select is disabled
   */
  isDisabled?: boolean;
  /**
   * Whether the select is required
   */
  isRequired?: boolean;
  /**
   * Whether the select is optional
   */
  isOptional?: boolean;
  /**
   * Whether the select is in loading state
   */
  isLoading?: boolean;
  /**
   * Size of the select
   */
  size?: "small" | "medium" | "large";
  /**
   * Prefix element (icon or text)
   */
  prefix?: React.ReactNode;
  /**
   * Suffix element (icon or text)
   */
  suffix?: React.ReactNode;
  /**
   * Show clear button when value is selected
   */
  showClearButton?: boolean;
  /**
   * Callback when clear button is clicked
   */
  onClear?: () => void;
  /**
   * Show the selected option's leadingIcon in the input box
   * Only shown when an option is selected and has a leadingIcon
   */
  showLeadingIcon?: boolean;
  /**
   * Custom class for the container
   */
  containerClassName?: string;
  /**
   * Custom class for the label
   */
  labelClassName?: string;
  /**
   * Custom class for the trigger button
   */
  triggerClassName?: string;
  /**
   * Custom class for the dropdown menu
   */
  menuClassName?: string;
  /**
   * Width of the dropdown menu
   */
  menuWidth?: "auto" | "full" | string;
  /**
   * Section heading for the dropdown
   */
  sectionHeading?: string;
  /**
   * Empty state title
   */
  emptyTitle?: string;
  /**
   * Empty state description
   */
  emptyDescription?: string;
  /**
   * Show search icon in empty state
   */
  emptyIcon?: React.ReactNode;
  /**
   * Info heading for tooltip
   */
  infoHeading?: string;
  /**
   * Info description for tooltip
   */
  infoDescription?: string;
  /**
   * Custom Link component to render on the right side
   * If provided, this will be rendered instead of the default Link with linkText
   */
  LinkComponent?: React.ReactNode;
  /**
   * Link text
   */
  linkText?: string;
  /**
   * Link href
   */
  linkHref?: string;
  /**
   * Link click handler
   */
  onLinkClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}

export const Select = React.forwardRef<HTMLDivElement, SelectProps>(
  (
    {
      className,
      options = [],
      value: controlledValue,
      defaultValue,
      onChange,
      placeholder = "Select an option",
      label,
      helperText,
      errorText,
      successText,
      validationState = "none",
      isDisabled = false,
      isRequired = false,
      isOptional = false,
      isLoading = false,
      size = "medium",
      prefix,
      suffix,
      showClearButton = false,
      onClear,
      showLeadingIcon = false,
      containerClassName,
      labelClassName,
      triggerClassName,
      menuClassName,
      menuWidth = "full",
      sectionHeading,
      emptyTitle = "No options available",
      emptyDescription = "There are no options to select from.",
      emptyIcon,
      infoHeading,
      infoDescription,
      LinkComponent,
      linkText,
      linkHref,
      onLinkClick,
      ...props
    },
    ref
  ) => {
    const [uncontrolledValue, setUncontrolledValue] = React.useState<
      string | number | undefined
    >(defaultValue);
    const [isOpen, setIsOpen] = React.useState(false);
    const [dropdownPlacement, setDropdownPlacement] = React.useState<
      "top" | "bottom"
    >("bottom");
    const selectRef = React.useRef<HTMLDivElement>(null);
    const dropdownContainerRef = React.useRef<HTMLDivElement>(null);

    const value =
      controlledValue !== undefined ? controlledValue : uncontrolledValue;

    // Find the selected option
    const selectedOption = options.find((opt) => opt.value === value);
    const hasValue = value !== undefined && value !== "";

    // Determine which helper text to show
    const displayHelperText = errorText || successText || helperText;
    const currentValidationState = errorText
      ? "negative"
      : successText
      ? "positive"
      : validationState;

    const handleOpenChange = (newOpen: boolean) => {
      if (!isDisabled && !isLoading) {
        setIsOpen(newOpen);
      }
    };

    const toggleOpen = () => {
      handleOpenChange(!isOpen);
    };

    const handleSelect = (option: SelectOption) => {
      if (controlledValue === undefined) {
        setUncontrolledValue(option.value);
      }
      onChange?.(option.value, option);
      setIsOpen(false);
    };

    const handleClear = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (onClear) {
        onClear();
      } else {
        if (controlledValue === undefined) {
          setUncontrolledValue(undefined);
        }
        onChange?.("", {} as SelectOption);
      }
    };

    const updateDropdownPlacement = React.useCallback(() => {
      if (typeof window === "undefined") return;
      const trigger = selectRef.current;
      if (!trigger) return;

      const triggerRect = trigger.getBoundingClientRect();
      const spaceBelow = window.innerHeight - triggerRect.bottom;
      const spaceAbove = triggerRect.top;

      const dropdownHeight = dropdownContainerRef.current
        ? dropdownContainerRef.current.offsetHeight
        : 0;

      if (dropdownHeight === 0) {
        setDropdownPlacement(spaceBelow >= spaceAbove ? "bottom" : "top");
        return;
      }

      if (spaceBelow >= dropdownHeight || spaceBelow >= spaceAbove) {
        setDropdownPlacement("bottom");
      } else {
        setDropdownPlacement("top");
      }
    }, []);

    const attachDropdownListeners = React.useCallback(() => {
      if (!isOpen) return;
      if (typeof window === "undefined") return;
      let rafId = requestAnimationFrame(updateDropdownPlacement);

      const handleUpdate = () => updateDropdownPlacement();
      window.addEventListener("resize", handleUpdate);
      window.addEventListener("scroll", handleUpdate, true);

      return () => {
        cancelAnimationFrame(rafId);
        window.removeEventListener("resize", handleUpdate);
        window.removeEventListener("scroll", handleUpdate, true);
      };
    }, [isOpen, updateDropdownPlacement]);

    React.useEffect(() => {
      const detach = attachDropdownListeners();
      return () => {
        detach?.();
      };
    }, [attachDropdownListeners]);

    React.useEffect(() => {
      if (isOpen) {
        updateDropdownPlacement();
      }
    }, [isOpen, options.length, updateDropdownPlacement]);

    // Close dropdown when clicking outside
    React.useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          selectRef.current &&
          !selectRef.current.contains(event.target as Node)
        ) {
          handleOpenChange(false);
        }
      };

      if (isOpen) {
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
          document.removeEventListener("mousedown", handleClickOutside);
        };
      }
    }, [isOpen]);

    // Close on escape key
    React.useEffect(() => {
      const handleEscape = (event: KeyboardEvent) => {
        if (event.key === "Escape") {
          handleOpenChange(false);
        }
      };

      if (isOpen) {
        document.addEventListener("keydown", handleEscape);
        return () => {
          document.removeEventListener("keydown", handleEscape);
        };
      }
    }, [isOpen]);

    // Handle keyboard navigation
    React.useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent) => {
        if (isDisabled || isLoading) return;

        if (!isOpen && (event.key === "Enter" || event.key === " ")) {
          event.preventDefault();
          setIsOpen(true);
          return;
        }

        if (isOpen) {
          if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            const currentIndex = options.findIndex(
              (opt) => opt.value === value
            );
            const nextIndex =
              event.key === "ArrowDown"
                ? Math.min(currentIndex + 1, options.length - 1)
                : Math.max(currentIndex - 1, 0);

            if (options[nextIndex] && !options[nextIndex].isDisabled) {
              handleSelect(options[nextIndex]);
            }
          }
        }
      };

      if (selectRef.current) {
        selectRef.current.addEventListener("keydown", handleKeyDown);
        return () => {
          selectRef.current?.removeEventListener("keydown", handleKeyDown);
        };
      }
    }, [isOpen, value, options, isDisabled, isLoading]);

    // Transform options to dropdown menu items
    const menuItems: DropdownMenuItem[] = options.map((option) => ({
      value: option.value,
      label: option.label ?? String(option.value),
      description: option.description,
      leadingIcon: option.leadingIcon,
      trailingIcon: option.trailingIcon,
      isDisabled: option.isDisabled,
      variant: option.variant,
      onClick: () => handleSelect(option),
    }));

    const widthStyle =
      menuWidth === "full" ? "100%" : menuWidth === "auto" ? "auto" : menuWidth;

    const sizeConfig = {
      small: {
        gap: "gap-2",
      },
      medium: {
        gap: "gap-2",
      },
      large: {
        gap: "gap-3",
      },
    };

    return (
      <div
        className={cn(
          "w-full flex flex-col",
          sizeConfig[size].gap,
          containerClassName
        )}
      >
        {label && (
          <FormHeader
            label={label}
            size={size}
            isRequired={isRequired}
            isOptional={isOptional}
            infoHeading={infoHeading}
            infoDescription={infoDescription}
            LinkComponent={LinkComponent}
            linkText={linkText}
            linkHref={linkHref}
            onLinkClick={onLinkClick}
            htmlFor={props.id}
            className="mb-2"
            labelClassName={labelClassName}
          />
        )}

        <div
          ref={selectRef}
          className={cn(
            selectVariants({
              size,
              validationState: currentValidationState,
              isDisabled,
            }),
            "relative w-full cursor-pointer",
            className
          )}
          onClick={!isDisabled && !isLoading ? toggleOpen : undefined}
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-disabled={isDisabled}
          {...props}
        >
          {/* Show prefix if provided, otherwise show selected option's leadingIcon if showLeadingIcon is true */}
          {prefix ? (
            <span
              className={cn(
                "shrink-0 flex items-center",
                isDisabled
                  ? "text-surface-ink-neutral-disabled"
                  : currentValidationState === "positive"
                  ? "text-feedback-ink-positive-intense"
                  : currentValidationState === "negative"
                  ? "text-feedback-ink-negative-subtle"
                  : "text-surface-ink-neutral-muted"
              )}
            >
              {prefix}
            </span>
          ) : showLeadingIcon && selectedOption?.leadingIcon ? (
            <span
              className={cn(
                "shrink-0 flex items-center",
                isDisabled
                  ? "text-surface-ink-neutral-disabled"
                  : currentValidationState === "positive"
                  ? "text-feedback-ink-positive-intense"
                  : currentValidationState === "negative"
                  ? "text-feedback-ink-negative-subtle"
                  : "text-surface-ink-neutral-muted"
              )}
            >
              {selectedOption.leadingIcon}
            </span>
          ) : null}

          <span
            className={cn(
              "flex-1 text-left truncate",
              !selectedOption && "text-surface-ink-neutral-muted",
              isDisabled && "text-surface-ink-neutral-disabled"
            )}
          >
            {isLoading ? "Loading..." : selectedOption?.label || placeholder}
          </span>

          {showClearButton && hasValue && !isDisabled && !isLoading && (
            <button
              type="button"
              onClick={handleClear}
              className="shrink-0 flex items-center justify-center text-surface-ink-neutral-muted hover:text-surface-ink-neutral-normal transition-colors"
              tabIndex={-1}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 4L4 12M4 4L12 12"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          )}

          {suffix && !showClearButton && (
            <span
              className={cn(
                "shrink-0 flex items-center",
                isDisabled
                  ? "text-surface-ink-neutral-disabled"
                  : currentValidationState === "positive"
                  ? "text-feedback-ink-positive-intense"
                  : currentValidationState === "negative"
                  ? "text-feedback-ink-negative-subtle"
                  : "text-surface-ink-neutral-muted"
              )}
            >
              {suffix}
            </span>
          )}

          <ChevronDown
            className={cn(
              "shrink-0 w-4 h-4 transition-transform",
              isDisabled
                ? "text-surface-ink-neutral-disabled"
                : currentValidationState === "positive"
                ? "text-feedback-ink-positive-intense"
                : currentValidationState === "negative"
                ? "text-feedback-ink-negative-subtle"
                : "text-surface-ink-neutral-muted",
              isOpen && "transform rotate-180"
            )}
          />

          {/* Dropdown Menu */}
          {isOpen && !isDisabled && !isLoading && (
            <div
              ref={dropdownContainerRef}
              className={cn(
                "absolute z-50 left-0 right-0",
                dropdownPlacement === "bottom"
                  ? "top-full mt-1"
                  : "bottom-full mb-1"
              )}
            >
              <DropdownMenu
                ref={ref}
                items={menuItems}
                sectionHeading={sectionHeading}
                isEmpty={options.length === 0}
                emptyTitle={emptyTitle}
                emptyDescription={emptyDescription}
                emptyIcon={emptyIcon}
                disableFooter={true}
                onClose={() => handleOpenChange(false)}
                className={menuClassName}
                width={widthStyle}
              />
            </div>
          )}
        </div>

        <FormFooter
          helperText={displayHelperText}
          validationState={
            currentValidationState === "none"
              ? "default"
              : currentValidationState
          }
          size={size}
          isDisabled={isDisabled}
          className="mt-1"
        />
      </div>
    );
  }
);

Select.displayName = "Select";

export { selectVariants };
